import { useCallback, useEffect, useRef } from "react";

import {
  OPENAI_YAML_TOOL_LIST_SCROLL_ANIMATION_MS,
  OPENAI_YAML_TOOL_LIST_SCROLL_DELAY_MS,
} from "./form-constants";

export const useToolListAutoScroll = <T,>(items: T[]) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastItemRef = useRef<HTMLDivElement>(null);
  const prevItemsLength = useRef(items.length);

  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const targetScrollTop = container.scrollHeight - container.clientHeight;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / OPENAI_YAML_TOOL_LIST_SCROLL_ANIMATION_MS, 1);
      const easeProgress = 1 - (1 - progress) ** 3;

      container.scrollTop =
        container.scrollTop + (targetScrollTop - container.scrollTop) * easeProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }, []);

  useEffect(() => {
    if (items.length > prevItemsLength.current) {
      const timeoutId = window.setTimeout(() => {
        scrollToBottom();
      }, OPENAI_YAML_TOOL_LIST_SCROLL_DELAY_MS);
      prevItemsLength.current = items.length;
      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    prevItemsLength.current = items.length;
    return undefined;
  }, [items.length, scrollToBottom]);

  return { scrollContainerRef, lastItemRef };
};
